import { type ActionEvent, Controller } from "@hotwired/stimulus"

import CarouselController from "../carousel_controller"
import DrawerController from "../drawer_controller"

import { formatDMMM } from "@/helpers/date_format"

export default class extends Controller {
  static outlets = ["carousel", "drawer"]
  declare readonly carouselOutlet: CarouselController
  declare readonly hasCarouselOutlet: boolean
  declare readonly drawerOutlet: DrawerController

  static targets = ["calendarMonth", "dateInput", "dateRadio", "dateSummary"]
  declare readonly calendarMonthTargets: HTMLTableElement[]
  declare readonly dateInputTarget: HTMLInputElement
  declare readonly dateRadioTargets: HTMLInputElement[]
  declare readonly dateSummaryTarget: HTMLElement

  // Manually track selected radio so we can un-check it on click.
  declare selectedDateRadio?: HTMLInputElement

  connect(): void {
    this.selectedDateRadio = this.dateRadioTargets.find((radio) => radio.checked)
  }

  deselectDate(): void {
    this.selectedDateRadio?.click()
  }

  deselectIfEmpty({ currentTarget }: ActionEvent): void {
    if (!(currentTarget as HTMLInputElement).value) {
      this.deselectDate()
    }
  }

  get isDateSelected(): boolean {
    return !!this.selectedDateRadio
  }

  jumpToSelectedDate(): void {
    if (this.isModal) {
      const month = this.selectedDateRadio
        ? this.calendarMonthTargets.find((month) => month.contains(this.selectedDateRadio!))
        : this.calendarMonthTargets.at(0)
      const calendar = month!.parentElement!

      // We use `scrollTop` on parent instead of `scrollIntoView` on element because the latter
      // will try to scroll ancestor containers rather than the immediate parent, and we can't
      // hide overflow on those ancestors without breaking the auto drawer height that fills
      // the available modal space.
      calendar.scrollTop = month!.offsetTop - calendar.offsetTop
    } else {
      const selectedDateCarouselSlideIndex = this.selectedDateRadio
        ? this.carouselOutlet.slideListItemTargets.findIndex((slide) =>
            slide.contains(this.selectedDateRadio as Node)
          )
        : 0

      window.requestAnimationFrame(() => {
        this.carouselOutlet.jumpToIndex(selectedDateCarouselSlideIndex)
      })
    }
  }

  openDateDrawer(): void {
    // Scroll to selected date before opening drawer to reduce jumping.
    this.jumpToSelectedDate()
    this.drawerOutlet.open()
  }

  selectDate({ currentTarget }: ActionEvent): void {
    const dateRadio = currentTarget as HTMLInputElement

    // Un-check radio if already selected
    if (dateRadio === this.selectedDateRadio) {
      dateRadio.checked = false
      this.selectedDateRadio = undefined
    } else {
      this.selectedDateRadio = dateRadio
    }

    this.setDateSummary()
  }

  setDateSummary(): void {
    const { selectedDateRadio } = this
    const summaryText = selectedDateRadio ? formatDMMM(new Date(selectedDateRadio.value)) : "Any"

    if (this.isModal) {
      this.dateSummaryTarget.textContent = summaryText
    } else {
      this.dateInputTarget.value = summaryText
    }
  }

  private get isModal(): boolean {
    return !this.hasCarouselOutlet
  }
}
